import { useEffect, useState, useRef } from "react";

/**
 * Hook customizado para animação de placeholder com sugestões rotativas para workout plans
 */

const placeholderSuggestions = [
  "Criar plano de hipertrofia com até 5 treinos (A, B, C, D, E), 3x por semana, peito/tríceps, costas/bíceps, pernas/ombros...",
  "Plano de treino para iniciante, 4x por semana, foco em força e resistência, com até 5 treinos...",
  "Treino de definição muscular, 5x por semana, divisão ABCDE (até 5 treinos)...",
  "Criar treino full body, 3x por semana, com até 3-4 treinos para aluno intermediário...",
  "Plano de treino para emagrecimento, 4x por semana, cardio e musculação, organizado em até 5 treinos...",
];

interface UseAnimatedPlaceholderWorkoutProps {
  isOpen: boolean;
  isAIModalOpen: boolean;
  aiInputValue: string;
}

export function useAnimatedPlaceholderWorkout({
  isOpen,
  isAIModalOpen,
  aiInputValue,
}: UseAnimatedPlaceholderWorkoutProps) {
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const animationRef = useRef<NodeJS.Timeout | null>(null);
  const charIndexRef = useRef(0);
  const isDeletingRef = useRef(false);

  useEffect(() => {
    if (!isOpen || isAIModalOpen || aiInputValue) {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
      setAnimatedPlaceholder("");
      return;
    }

    const currentSuggestion = placeholderSuggestions[suggestionIndex];
    charIndexRef.current = 0;
    isDeletingRef.current = false;

    const animate = () => {
      if (
        !isDeletingRef.current &&
        charIndexRef.current <= currentSuggestion.length
      ) {
        setAnimatedPlaceholder(
          currentSuggestion.slice(0, charIndexRef.current)
        );
        charIndexRef.current++;
        animationRef.current = setTimeout(animate, 50);
      } else if (
        !isDeletingRef.current &&
        charIndexRef.current > currentSuggestion.length
      ) {
        // Espera antes de começar a deletar
        animationRef.current = setTimeout(() => {
          isDeletingRef.current = true;
          charIndexRef.current = currentSuggestion.length;
          animate();
        }, 2000);
      } else if (isDeletingRef.current && charIndexRef.current > 0) {
        charIndexRef.current--;
        setAnimatedPlaceholder(
          currentSuggestion.slice(0, charIndexRef.current)
        );
        animationRef.current = setTimeout(animate, 30);
      } else {
        // Passa para próxima sugestão
        setSuggestionIndex(
          (prev) => (prev + 1) % placeholderSuggestions.length
        );
        setAnimatedPlaceholder("");
      }
    };

    animationRef.current = setTimeout(animate, 500);

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isOpen, isAIModalOpen, aiInputValue, suggestionIndex]);

  return animatedPlaceholder;
}
